import React, {
  ReactElement,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import AppSidebar, {
  IAppSidebar,
  IAppSidebarInnerRef,
} from 'shared/components/AppSidebar';
import { Outlet, useNavigate } from 'react-router';
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { DefaultSidebar } from 'shared/hooks/Layout';
import { connect, useDispatch } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthActions } from 'state/authentication/Action';
import { useLocation } from 'react-router-dom';
import { BRAND } from 'shared/Constants';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { NotificationService } from 'services/NotificationService';
import { Utils } from 'shared/helpers';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import { actionOnNavigate } from 'shared/helpers/Notification';
// import useSize from 'shared/hooks/useSize';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Renders the Layout component.
 * @returns {ReactElement} The rendered component.
 */

export interface INotifications {
  _id: string;
  title: string;
  event: string;
  isActive: boolean;
  isArchived: boolean;
  createdBy: string;
  userId: string;
  subEvent: string;
  deletedAt: any;
  descriptions: string;
  readAt: any;
  tag: string;
  archivedAt: any;
  organizationId: string;
  notificationEvents: any;
  createdAt: string;
  updatedAt: string;
  __v: number;
  isExpanded: boolean;
  action: string | null;
}

const NAV_HEIGHT = 84;

const Layout = (): ReactElement => {
  const reduxDispatch = useDispatch();
  const location = useLocation();
  // const { isDesktop } = useSize();

  const isBelow1024 = useMediaQuery('(max-width: 1023px)');
  const isDesktopSidebarVisible = !isBelow1024;

  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [notification, setNotification] = useState<INotifications[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationCount, setNotificationCound] = useState('');
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(true);

  const navRef = useRef<HTMLDivElement | null>(null);
  const [navHeight, setNavHeight] = useState<number>(NAV_HEIGHT);

  // API calls
  const getNotificationList = (): void => {
    NotificationService.getNotificationList('false', '', '', '')
      .then((res) => {
        const { notifications, unreadCount } = res.data.data;
        const updatedNotification = notifications.map((d: INotifications) => ({
          ...d,
          isExpanded: false,
        }));
        setNotification(updatedNotification);
        setNotificationCound(unreadCount);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const loadConfigs = (): void => {
    getNotificationList();
    setIsLoadingConfig(true);
    setTimeout(() => {
      setIsLoadingConfig(false);
    }, 1000);
  };

  const isFullScreen =
    location.pathname.includes('/ai-risk-baseline/survey/') ||
    location.pathname.includes('/ai-risk-baseline/new');
  /**
   * Props for the AppSidebarV2 component.
   * @type {IAppSidebar}
   */
  const sidebarInnerRef = useRef<IAppSidebarInnerRef | null>(null);
  const [appSidebarProps] = useState<IAppSidebar>({
    ref: sidebarInnerRef,
    children: <Box />,
    menus: DefaultSidebar,
    pathname: '',
    onUserLoggingOut: () => {
      reduxDispatch(AuthActions.doLogout(null));
    },
    onUserSettingsClick: () => {},
    onMenuChange: () => {},
    onOrgChange: () => {
      loadConfigs();
    },
  });

  const scrollToTop = (): void => {
    const element = document.getElementsByClassName('app-right-container');
    if (element) {
      element[0].scrollTo(0, 0);
    }
  };

  const getTimeAgo = (date: string): string => moment(date).fromNow();

  // UI Actions

  const actionOnMarkAsRead = (id: string): void => {
    NotificationService.updateNotification({ event: 'read' }, id)
      .then(() => {
        getNotificationList();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const actionOnOpen = (event: any): void => {
    setAnchorEl(event.currentTarget);
    getNotificationList();
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const actionOnNotification = (): void => {
    navigate('/notifications', { replace: true });
    setAnchorEl(null);
    setIsLoadingConfig(false);
  };

  const handleDrawerOpen = (): void => {
    setMobileOpen(true);
  };

  const handleDrawerClose = (): void => {
    setMobileOpen(false);
  };

  useEffect(() => {
    const updateHeight = (): void => {
      const h = navRef.current?.offsetHeight;
      if (h && h > 0) setNavHeight(h);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [isBelow1024]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isBelow1024 && mobileOpen) setMobileOpen(false);
  }, [isBelow1024, mobileOpen]);

  // Hooks
  useEffect(() => {
    scrollToTop();
  }, [location.pathname]);

  useEffect(() => {
    getNotificationList();
  }, []);

  // UI Elements
  return (
    <>
      {!isFullScreen && (
        <Box
          className="nav-menu-header"
          sx={{
            px: {
              xs: 2,
              sm: 4,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isBelow1024 && (
              <IconButton
                onClick={mobileOpen ? handleDrawerClose : handleDrawerOpen}
                aria-label={
                  mobileOpen ? 'close navigation menu' : 'open navigation menu'
                }
                aria-controls="mobile-sidebar-drawer"
                aria-expanded={mobileOpen}
                sx={{
                  zIndex: 9999,
                  color: '#fff',
                }}
              >
                {mobileOpen ? (
                  <CloseIcon sx={{ fontSize: 32, color: '#fff' }} />
                ) : (
                  <MenuIcon sx={{ fontSize: 32, color: '#fff' }} />
                )}
              </IconButton>
            )}
            {/* <img src={BRAND.LOGO} width={200} alt="Quixx Logo" /> */}
            <Box
              component="img"
              src={BRAND.LOGO}
              alt="Quixx Logo"
              sx={{
                width: {
                  xs: 120,
                  sm: 200,
                },
                height: 'auto',
              }}
            />
          </Box>
          <Box>
            <Tooltip title="Help Centre">
              <IconButton
                onClick={() => {
                  window.open(
                    'https://help-clarityai.doxai.co/support/solutions/24000004496',
                    'clarityai_book'
                  );
                }}
              >
                <MenuBookIcon htmlColor="#d1f27a" />
              </IconButton>
            </Tooltip>
            <IconButton onClick={(e) => actionOnOpen(e)}>
              <Badge badgeContent={notificationCount} sx={{ color: '#d1f27a' }}>
                <NotificationsIcon sx={{ color: '#d1f27a' }} />
              </Badge>
            </IconButton>
          </Box>
        </Box>
      )}

      <Box className={!isFullScreen ? 'app-container' : ''}>
        {!isFullScreen && isDesktopSidebarVisible && (
          <Box className="app-left-container">
            <AppSidebar {...appSidebarProps} />
          </Box>
        )}

        <Box className="app-right-container">
          {isLoadingConfig && (
            <Box className="empty-content">
              <CircularProgress />
            </Box>
          )}
          {!isLoadingConfig && (
            <Suspense
              fallback={
                <Box className="align-loader">
                  <CircularProgress
                    title="progress loading"
                    className="mr-2 margin-loader"
                    size={54}
                    color="inherit"
                  />
                </Box>
              }
            >
              <Outlet />
            </Suspense>
          )}
        </Box>
      </Box>

      {isBelow1024 && !isFullScreen && (
        <Drawer
          id="mobile-sidebar-drawer"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiBackdrop-root': {
              top: `${navHeight}px`,
            },

            '& .MuiDrawer-paper': {
              top: `${navHeight}px`,
              height: `calc(100% - ${navHeight}px)`,
              width: 320,
              maxWidth: '85vw',
              bgcolor: 'background.paper',
              boxSizing: 'border-box',
              borderRadius: 0,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              border: 0,
              boxShadow: '2px 0 12px rgba(0,0,0,0.15)',
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <AppSidebar
              {...appSidebarProps}
              // onMenuClick={handleDrawerClose}
            />
          </Box>
        </Drawer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            overflow: 'hidden',
          },
        }}
      >
        <Box>
          <Box px={3} py={2}>
            <Typography variant="h5" fontWeight={600}>
              Notifications
            </Typography>
          </Box>

          <Divider />
          <Box
            sx={{
              maxHeight: 300,
              overflowY: 'auto',
            }}
          >
            {notification.map((note) => (
              <MenuItem
                sx={{ display: 'block' }}
                onClick={() => {
                  actionOnMarkAsRead(note._id);
                  actionOnNavigate(note, navigate);
                  setAnchorEl(null);
                }}
              >
                <Stack spacing={2} direction="row">
                  <InfoOutlinedIcon />
                  <Stack>
                    <Typography variant="body1" className="fw-7">
                      {note.title}
                    </Typography>
                    <Typography variant="body2">{note.descriptions}</Typography>
                    <Typography variant="body2">
                      {getTimeAgo(note.updatedAt)}
                    </Typography>
                  </Stack>
                </Stack>
              </MenuItem>
            ))}
            {notification.length === 0 && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={1}
                sx={{ overflowY: 'scroll', height: '150px' }}
              >
                <NotificationsNoneOutlinedIcon
                  sx={{ fontSize: 50, color: '#bdbdbd' }}
                />

                <Typography variant="body1" color="text.secondary">
                  No unread notifications
                </Typography>
              </Box>
            )}
          </Box>

          <>
            <Divider />

            <Box p={2}>
              <Button
                className="btn-small"
                fullWidth
                onClick={actionOnNotification}
              >
                View all notifications
              </Button>
            </Box>
          </>
        </Box>
      </Menu>
    </>
  );
};
const mapStateToProps = (state: any): any => ({
  baseState: state.baseAppState,
});

export default connect(mapStateToProps, null)(Layout);
